import type { ColDef } from 'ag-grid-community'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { AttendancePlayer } from '../types'
import AttendanceSortHeader, {
  REGROUP_SORT_KEY,
  type AttendanceSortHeaderParams,
} from '../components/cells/AttendanceSortHeader'
import AvailabilityCell from '../components/cells/AvailabilityCell'
import ConfirmCell from '../components/cells/ConfirmCell'
import JerseyCell from '../components/cells/JerseyCell'
import NameCell from '../components/cells/NameCell'
import PromoteCell from '../components/cells/PromoteCell'
import SpareStatusCell from '../components/cells/SpareStatusCell'
import StartingCell from '../components/cells/StartingCell'

// Cell classes styled by AttendanceSection's grid wrapper.
export const ATTENDANCE_CELL_CLASS = 'attendance-cell'
export const ATTENDANCE_CENTER_CLASS = 'attendance-cell-center'

// Clicking these (non-interactive) columns counts as pressing the row. The
// other columns hold controls whose clicks must not.
export const ROW_PRESS_COLUMN_IDS = ['position', 'name', 'status']

export interface UseAttendanceColumnsParams {
  isSpareCandidate: boolean
  hideConfirm: boolean
  showStartingColumn: boolean
  startingLabel: string
}

const sortHeader = (
  sortKey: string
): Pick<
  ColDef<AttendancePlayer>,
  'headerComponent' | 'headerComponentParams'
> => {
  const headerComponentParams: AttendanceSortHeaderParams = { sortKey }
  return { headerComponent: AttendanceSortHeader, headerComponentParams }
}

const centered: Pick<ColDef<AttendancePlayer>, 'cellClass' | 'headerClass'> = {
  cellClass: [ATTENDANCE_CELL_CLASS, ATTENDANCE_CENTER_CLASS],
  headerClass: ATTENDANCE_CENTER_CLASS,
}

/** Column set for one attendance section, shaped by the section's flags. */
export const useAttendanceColumns = ({
  isSpareCandidate,
  hideConfirm,
  showStartingColumn,
  startingLabel,
}: UseAttendanceColumnsParams): ColDef<AttendancePlayer>[] => {
  'use memo'

  const columns: ColDef<AttendancePlayer>[] = []

  if (!isSpareCandidate) {
    columns.push({
      colId: 'number',
      headerName: '#',
      width: 88,
      cellRenderer: JerseyCell,
      ...sortHeader('number'),
    })
  }

  columns.push(
    {
      colId: 'position',
      headerName: capitalizeWords('pos'),
      width: 88,
      valueGetter: ({ data }) => data?.player_type?.abbreviation ?? '',
      ...sortHeader('position'),
    },
    {
      colId: 'name',
      headerName: capitalizeWords('name'),
      flex: 1,
      minWidth: 200,
      cellRenderer: NameCell,
      ...sortHeader('name'),
    }
  )

  if (showStartingColumn) {
    columns.push({
      colId: 'starting',
      headerName: capitalizeWords(startingLabel),
      width: 152,
      cellRenderer: StartingCell,
      ...centered,
    })
  }

  columns.push(
    isSpareCandidate
      ? {
          colId: 'status',
          headerName: capitalizeWords('status'),
          width: 128,
          cellRenderer: SpareStatusCell,
          ...centered,
        }
      : {
          colId: 'available',
          headerName: capitalizeWords('available'),
          width: 152,
          cellRenderer: AvailabilityCell,
          ...centered,
          ...sortHeader(REGROUP_SORT_KEY),
        }
  )

  if (!hideConfirm) {
    columns.push({
      colId: 'confirm',
      headerName: capitalizeWords(isSpareCandidate ? 'promote' : 'confirm'),
      width: 120,
      cellRenderer: isSpareCandidate ? PromoteCell : ConfirmCell,
      ...centered,
    })
  }

  return columns
}
